import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  ArrowLeft,
  CalendarBlank,
  Flask,
  Check,
  Info,
  Warning
} from '@phosphor-icons/react';

// Strict hormone validation ranges (must match backend HORMONE_RANGES)
const HORMONE_RANGES = {
  fsh: { min: 1, max: 150, unit: 'mIU/mL', label: 'FSH' },
  lh: { min: 1, max: 100, unit: 'mIU/mL', label: 'LH' },
  estradiol_e2: { min: 5, max: 800, unit: 'pg/mL', label: 'Estradiol (E2)' },
  amh: { min: 0.01, max: 15, unit: 'ng/mL', label: 'AMH' },
  inhibin_b: { min: 5, max: 300, unit: 'pg/mL', label: 'Inhibin B' },
  progesterone: { min: 0.1, max: 30, unit: 'ng/mL', label: 'Progesterone' }
};

// Validate hormone values - strict validation
const validateHormones = (data) => {
  const errors = [];
  
  for (const [key, range] of Object.entries(HORMONE_RANGES)) {
    const value = data[key];
    if (value !== '' && value !== null && value !== undefined) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        errors.push(`${range.label}: Please enter a valid number`);
      } else if (numValue < 0) {
        errors.push(`${range.label}: Value cannot be negative`);
      } else if (numValue < range.min || numValue > range.max) {
        errors.push(`${range.label}: ${numValue} ${range.unit} is outside the expected range (${range.min}-${range.max} ${range.unit}). Please verify your lab report.`);
      }
    }
  }
  
  return errors;
};

export default function DataEntry() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [formData, setFormData] = useState({
    estradiol_e2: '',
    fsh: '',
    progesterone: '',
    inhibin_b: '',
    lh: '',
    amh: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setValidationErrors([]); // Clear errors when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors([]);
    
    // Check if at least one hormone value is entered
    const hasAnyHormone = Object.values(formData).some(value => value !== '');
    if (!hasAnyHormone) {
      toast.error('Please enter at least one hormone value');
      return;
    }
    
    // Strict validation
    const hormoneErrors = validateHormones(formData);
    if (hormoneErrors.length > 0) {
      setValidationErrors(hormoneErrors);
      toast.error('Please fix the validation errors before saving');
      return;
    }
    
    setLoading(true);

    try {
      const data = {
        test_date: format(date, 'yyyy-MM-dd'),
        estradiol_e2: formData.estradiol_e2 ? parseFloat(formData.estradiol_e2) : null,
        fsh: formData.fsh ? parseFloat(formData.fsh) : null,
        progesterone: formData.progesterone ? parseFloat(formData.progesterone) : null,
        inhibin_b: formData.inhibin_b ? parseFloat(formData.inhibin_b) : null,
        lh: formData.lh ? parseFloat(formData.lh) : null,
        amh: formData.amh ? parseFloat(formData.amh) : null
      };

      await api.createHormonalData(data);
      toast.success('Hormonal data saved successfully!');
      // Force dashboard to reload and show analysis reminder
      navigate('/dashboard', { replace: true, state: { refresh: Date.now(), dataUpdated: true } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#5B21B6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const hormoneFields = [
    {
      name: 'estradiol_e2',
      label: 'Estradiol (E2)',
      unit: 'pg/mL',
      description: 'Primary female hormone, declines during menopause',
      normalRange: '30-400 pg/mL (varies by cycle phase)',
      expectedMin: HORMONE_RANGES.estradiol_e2.min,
      expectedMax: HORMONE_RANGES.estradiol_e2.max,
      warningLow: 30,
      warningHigh: 400
    },
    {
      name: 'fsh',
      label: 'FSH (Follicle Stimulating Hormone)',
      unit: 'mIU/mL',
      description: 'Rises during menopause transition',
      normalRange: '<25 mIU/mL (premenopause), >40 mIU/mL (postmenopause)',
      expectedMin: HORMONE_RANGES.fsh.min,
      expectedMax: HORMONE_RANGES.fsh.max,
      warningLow: 3.5,
      warningHigh: 12.5
    },
    {
      name: 'progesterone',
      label: 'Progesterone',
      unit: 'ng/mL',
      description: 'Hormone that supports pregnancy, declines with age',
      normalRange: '5-20 ng/mL (luteal phase)',
      expectedMin: HORMONE_RANGES.progesterone.min,
      expectedMax: HORMONE_RANGES.progesterone.max,
      warningLow: 0.2,
      warningHigh: 25
    },
    {
      name: 'inhibin_b',
      label: 'Inhibin B',
      unit: 'pg/mL',
      description: 'Early marker of ovarian reserve decline',
      normalRange: '45-200 pg/mL',
      expectedMin: HORMONE_RANGES.inhibin_b.min,
      expectedMax: HORMONE_RANGES.inhibin_b.max,
      warningLow: 20,
      warningHigh: 150
    },
    {
      name: 'lh',
      label: 'LH (Luteinizing Hormone)',
      unit: 'mIU/mL',
      description: 'Works with FSH to regulate menstrual cycle',
      normalRange: '5-25 mIU/mL',
      expectedMin: HORMONE_RANGES.lh.min,
      expectedMax: HORMONE_RANGES.lh.max,
      warningLow: 2.4,
      warningHigh: 12.6
    },
    {
      name: 'amh',
      label: 'AMH (Anti-Müllerian Hormone)',
      unit: 'ng/mL',
      description: 'Best predictor of ovarian reserve',
      normalRange: '1.0-3.5 ng/mL (varies by age)',
      expectedMin: HORMONE_RANGES.amh.min,
      expectedMax: HORMONE_RANGES.amh.max,
      warningLow: 1.0,
      warningHigh: 3.5
    }
  ];

  const getValueStatus = (field, value) => {
    if (!value || value === '') return null;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'invalid';
    if (numValue < field.expectedMin || numValue > field.expectedMax) return 'error';
    if (numValue < field.warningLow) return 'low';
    if (numValue > field.warningHigh) return 'high';
    return 'normal';
  };

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95]">
            <ArrowLeft className="w-5 h-5" weight="bold" />
            Back to Dashboard
          </Link>
          <Link to="/" className="text-xl font-bold text-[#5B21B6] font-['Outfit']">
            MenoPath
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] flex items-center justify-center">
              <Flask className="w-8 h-8 text-white" weight="duotone" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit']">
                Enter Test Results
              </h1>
              <p className="text-[#6B7280]">Input your latest hormonal blood test results</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Validation Errors Display */}
            {validationErrors.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-2xl p-4"
              >
                <div className="flex items-start gap-3">
                  <Warning className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" weight="fill" />
                  <div>
                    <h4 className="font-semibold text-red-700 mb-2">Please correct the following:</h4>
                    <ul className="space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index} className="text-sm text-red-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Test Date */}
            <div className="glass-card rounded-3xl p-6">
              <Label className="text-[#1E1B4B] font-semibold mb-3 block">Test Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full md:w-auto justify-start text-left font-normal h-12 rounded-xl border-2"
                    data-testid="test-date-btn"
                  >
                    <CalendarBlank className="mr-2 h-5 w-5 text-[#5B21B6]" weight="duotone" />
                    {date ? format(date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(newDate) => {
                      setDate(newDate || new Date());
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Hormone Fields */}
            <div className="glass-card rounded-3xl p-6">
              <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
                Hormone Levels
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {hormoneFields.map((field, index) => {
                  const status = getValueStatus(field, formData[field.name]);
                  const borderColor = status === 'error' ? 'border-red-500' : 
                                     status === 'low' || status === 'high' ? 'border-amber-400' : 
                                     status === 'normal' ? 'border-green-400' : 'border-gray-200';
                  
                  return (
                    <motion.div 
                      key={field.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="space-y-2"
                    >
                      <Label htmlFor={field.name} className="text-[#1E1B4B] font-medium">
                        {field.label}
                      </Label>
                      <div className="relative">
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="0.01"
                          value={formData[field.name]}
                          onChange={handleChange}
                          placeholder={`Enter ${field.unit}`}
                          className={`h-12 rounded-xl border-2 ${borderColor} focus:border-[#5B21B6] pr-20`}
                          data-testid={`${field.name}-input`}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#6B7280]">
                          {field.unit}
                        </span>
                      </div>
                      {/* Validation feedback */}
                      {status === 'error' && (
                        <p className="text-xs text-red-600 flex items-start gap-1">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" weight="fill" />
                          Value outside expected range (0 - {field.expectedMax} {field.unit})
                        </p>
                      )}
                      {status === 'low' && (
                        <p className="text-xs text-amber-600 flex items-start gap-1">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" weight="fill" />
                          Below normal range (Normal: {field.normalRange})
                        </p>
                      )}
                      {status === 'high' && (
                        <p className="text-xs text-amber-600 flex items-start gap-1">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" weight="fill" />
                          Above normal range (Normal: {field.normalRange})
                        </p>
                      )}
                      {status === 'normal' && (
                        <p className="text-xs text-green-600 flex items-start gap-1">
                          <Check className="w-3 h-3 mt-0.5 flex-shrink-0" weight="bold" />
                          Within normal range
                        </p>
                      )}
                      {!status && (
                        <p className="text-xs text-[#6B7280] flex items-start gap-1">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" weight="fill" />
                          {field.normalRange}
                        </p>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-[#EDE9FE] rounded-2xl p-6">
              <h3 className="font-semibold text-[#5B21B6] mb-2 flex items-center gap-2">
                <Info className="w-5 h-5" weight="fill" />
                Tips for Accurate Results
              </h3>
              <ul className="text-sm text-[#5B21B6]/80 space-y-1">
                <li>• Enter values exactly as shown on your lab report</li>
                <li>• FSH and LH are best measured on cycle day 2-4</li>
                <li>• AMH can be measured any time during your cycle</li>
                <li>• Leave fields empty if you don't have that test result</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Link to="/dashboard" className="flex-1 md:flex-none">
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full h-14 rounded-xl border-2 border-gray-200 text-[#6B7280]"
                >
                  Cancel
                </Button>
              </Link>
              <Button 
                type="submit"
                disabled={loading}
                className="flex-1 h-14 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl text-lg font-semibold"
                data-testid="save-hormones-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" weight="bold" />
                    Save Test Results
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
